import { useMemo } from "react";
import { ColumnConfig, DEFAULT_COLUMNS } from "../types";
import { useCardFieldLabels } from "./useCardFieldLabels";

/**
 * Hook that provides enhanced columns with dynamic label resolution
 * Updates the primary, secondary, and tertiary columns with field names from shop configuration
 */
export const useEnhancedColumns = (
  baseColumns: ColumnConfig[] = DEFAULT_COLUMNS
): ColumnConfig[] => {
  const {
    getPrimaryFieldLabel,
    getSecondaryFieldLabel,
    getTertiaryFieldLabel,
  } = useCardFieldLabels();

  return useMemo(() => {
    return baseColumns.map((column) => {
      // Add dynamic label resolvers for primary, secondary, and tertiary fields
      switch (column.id) {
        case "primary":
          return {
            ...column,
            labelResolver: getPrimaryFieldLabel,
            fallbackLabel: "Primary",
          };
        case "secondary":
          return {
            ...column,
            labelResolver: getSecondaryFieldLabel,
            fallbackLabel: "Secondary",
          };
        case "tertiary":
          return {
            ...column,
            labelResolver: getTertiaryFieldLabel,
            fallbackLabel: "Tertiary",
          };
        default:
          return column;
      }
    });
  }, [
    baseColumns,
    getPrimaryFieldLabel,
    getSecondaryFieldLabel,
    getTertiaryFieldLabel,
  ]);
};

/**
 * Helper function to get the display label for a column
 * Uses labelResolver if available, otherwise falls back to label or fallbackLabel
 */
export const getColumnDisplayLabel = (column: ColumnConfig): string => {
  const resolveRaw = (): string => {
    if (column.labelResolver) {
      try {
        return column.labelResolver() || column.fallbackLabel || column.label;
      } catch (error) {
        console.warn(`Error resolving label for column ${column.id}:`, error);
        return column.fallbackLabel || column.label;
      }
    }
    return column.label;
  };
  // Display-only trim: "Year, Make, Model of vehicle" is a stored card-content
  // field key used across the app (types, cards.ts, fieldMigration), so we only
  // drop the " of vehicle" suffix for the header label — never the key itself.
  return resolveRaw().replace(/ of vehicle$/, "");
};
